import * as React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Stack from "@mui/material/Stack";


export default function CustomMenuList({ menuData = defaultMenuData, buttonLabel, onSelect, onClick }) 
{
  const [rootOpen, setRootOpen] = React.useState(false);
  const rootAnchorRef = React.useRef(null);

  // anchorMap: key -> anchorEl (truthy => submenu open)
  const [anchorMap, setAnchorMap] = React.useState({});

  // stable key generator
  const safeKey = (parentPath, idx, id) => {
    const raw = `${parentPath}::${idx}::${id ?? idx}`;
    return raw.replace(/[^0-9A-Za-z_\-.:]/g, "_");
  };

  // derive parent path of a key (remove last two segments)
  const parentPathOf = (key) => {
    const parts = key.split("::");
    if (parts.length <= 2) return parts.slice(0, 1).join("::"); // 'root'
    return parts.slice(0, -2).join("::");
  };

  // open submenu for key and close sibling submenus at same level
  const openSubmenu = (key, anchorEl) => {
    const parentPath = parentPathOf(key);
    setAnchorMap((prev) => {
      const next = {};
      for (const k of Object.keys(prev)) {
        const kParent = parentPathOf(k);
        if (kParent !== parentPath) {
          next[k] = prev[k];
        }
      }
      next[key] = anchorEl;
      return next;
    });
  };

  // close submenu for key and any descendant keys
  const closeSubmenu = (key) => {
    setAnchorMap((m) => {
      const next = {};
      const prefix = `${key}::`;
      for (const k of Object.keys(m)) {
        if (k === key) continue;
        if (k.startsWith(prefix)) continue;
        next[k] = m[k];
      }
      return next;
    });
  };

  const closeAll = () => {
    setRootOpen(false);
    setAnchorMap({});
  };

  const handleRootToggle = () => {
    setRootOpen((prev) => {
      const next = !prev;
      if (!next) setAnchorMap({});
      return next;
    });
  };

  const handleLeafClick = (item) => {
    if (typeof onSelect === "function") {
      try {
        onSelect(item);
      } catch (err) {
        // ignore user callback errors
        // console.error(err);
      }
    }
    // support legacy `onClick` prop name used by callers
    if (typeof onClick === "function") {
      try {
        onClick(item);
      } catch (err) {
        // ignore
      }
    }
    closeAll();
  };

  const handleClickAwayRoot = (event) => {
    if (rootAnchorRef.current && rootAnchorRef.current.contains(event.target)) {
      return;
    }
    closeAll();
  };

  // recursive renderer for menu items (click-only)
  // We'll collect Popper elements for submenus in `poppers` during the walk and
  // render them separately so MenuList receives only MenuItem children.
  const poppers = [];
  const renderMenuItems = (items, parentPath = "root") =>
    items.map((item, idx) => {
      const key = safeKey(parentPath, idx, item.id);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      const isOpen = Boolean(anchorMap[key]);

      const handleToggleSub = (e) => {
        e.stopPropagation();
        const currentTarget = e.currentTarget;
        if (isOpen) {
          closeSubmenu(key);
        } else {
          openSubmenu(key, currentTarget);
        }
      };

      const handleItemClick = (e) => {
        e.stopPropagation();
        handleLeafClick(item);
      };

      // If the item has children, build a Popper for the submenu and push it into poppers array.
      if (hasChildren) {
        poppers.push(
          <Popper
            key={`popper-${key}-${poppers.length}`}
            id={`submenu-${key}`}
            open={isOpen}
            anchorEl={anchorMap[key]}
            placement="right-start"
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
                <Paper>
                  <ClickAwayListener
                    onClickAway={(e) => {
                      const anchorEl = anchorMap[key];
                      if (anchorEl && anchorEl.contains(e.target)) return;
                      closeSubmenu(key);
                    }}
                  >
                    <MenuList autoFocusItem={isOpen} id={`menu-list-${key}`}>
                      {renderMenuItems(item.children, key)}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        );
      }

      return (
        <MenuItem
          key={key}
          aria-haspopup={hasChildren ? "true" : undefined}
          aria-controls={hasChildren && isOpen ? `submenu-${key}` : undefined}
          onClick={hasChildren ? handleToggleSub : handleItemClick}
        >
          {item.value ?? item.label ?? ""}
        </MenuItem>
      );
    });

  return (
    <Stack direction="row" spacing={2}>
      <div>
        <Button
          ref={rootAnchorRef}
          aria-controls={rootOpen ? "composition-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={rootOpen ? "true" : undefined}
          onClick={handleRootToggle}
          color="inherit"
        >
          {buttonLabel}
        </Button>

        <Popper
          open={rootOpen}
          anchorEl={rootAnchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
              <Paper>
                <ClickAwayListener onClickAway={handleClickAwayRoot}>
                  <div>
                    <MenuList autoFocusItem={rootOpen} id="composition-menu">
                      {renderMenuItems(menuData, "root")}
                    </MenuList>
                    {/* render collected submenu Poppers outside the MenuList */}
                    {poppers}
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Stack>
  );
}